/**
 * Pure-парсер SMF (Standard MIDI File) → `Midi`.
 *
 * Особенности:
 * - толерантен к диким meta-длинам (`FF 21 00`, `FF 51 00` и т.п.):
 *   при расхождении VLQ-длины с ожидаемой для известных meta-типов
 *   недостающие байты дописываются нулями, лишние — обрезаются;
 * - поддерживает running status для канальных событий;
 * - текстовые meta и SysEx читаются «как есть»;
 * - SysEx F0/F7 сохраняется в `track.sysex` без декодирования;
 * - вычисляет `durationSeconds` по последнему тику и карте темпо-событий;
 * - все ошибки бросает как `MidiParseError` с `position`.
 */

import type {
  Midi,
  MidiChannel,
  MidiControlChange,
  MidiHeader,
  MidiInstrument,
  MidiMetaEvent,
  MidiPitchBend,
  MidiProgramChange,
  MidiSysEx,
  MidiTempo,
  MidiTrack,
  MidiUnknownMeta,
} from "./types.js";
import { DRUM_CHANNEL, MIDI_VELOCITY_MAX } from "./types.js";
import { MidiParseError } from "./errors.js";
import {
  REQUIRED_LENGTHS,
  TEXT_META_TYPES,
  UNKNOWN_META_TYPES,
  decodeUtf8,
  metaTypeName,
  parseKeySignatureEvent,
  parseMetaEvent,
  parseTempoEvent,
  parseTimeSignatureEvent,
} from "./internal/meta-events.js";
import { readVLQ } from "./internal/vlq.js";
import { getInstrumentMeta } from "./instruments.js";

const MTHD = [0x4d, 0x54, 0x68, 0x64];
const MTRK_BYTES = [0x4d, 0x54, 0x72, 0x6b];

const readUint16BE = (input: Uint8Array, pos: number): number =>
  ((input[pos]! << 8) | input[pos + 1]!) & 0xffff;

const readUint32BE = (input: Uint8Array, pos: number): number =>
  ((input[pos]! << 24) |
    (input[pos + 1]! << 16) |
    (input[pos + 2]! << 8) |
    input[pos + 3]!) >>> 0;

const startsWith = (input: Uint8Array, pos: number, sig: readonly number[]): boolean => {
  if (pos + sig.length > input.length) return false;
  for (let i = 0; i < sig.length; i += 1) {
    if (input[pos + i] !== sig[i]) return false;
  }
  return true;
};

const tickToSeconds = (
  tick: number,
  ppq: number,
  tempoMap: ReadonlyArray<MidiTempo>
): number => {
  if (tick <= 0) return 0;
  let seconds = 0;
  let prevTick = 0;
  let currentBpm = tempoMap.length > 0 ? tempoMap[0]!.bpm : 120;
  for (const t of tempoMap) {
    if (t.ticks >= tick) break;
    if (t.ticks > prevTick) {
      seconds += ((t.ticks - prevTick) / ppq) * (60 / currentBpm);
      prevTick = t.ticks;
    }
    currentBpm = t.bpm;
  }
  if (prevTick < tick) {
    seconds += ((tick - prevTick) / ppq) * (60 / currentBpm);
  }
  return seconds;
};

const dedupByTick = <T extends { ticks: number }>(arr: T[]): void => {
  const seen = new Map<number, T>();
  for (const item of arr) {
    seen.set(item.ticks, item);
  }
  arr.length = 0;
  for (const item of seen.values()) arr.push(item);
};

const dedupMeta = (
  arr: { ticks: number; type: string; text?: string }[]
): void => {
  const seen = new Map<string, { ticks: number; type: string; text?: string }>();
  for (const m of arr) {
    const key = `${m.ticks}|${m.type}|${m.text ?? ""}`;
    seen.set(key, m);
  }
  arr.length = 0;
  for (const item of seen.values()) arr.push(item);
};

const DEFAULT_INSTRUMENT: MidiInstrument = {
  family: "piano",
  number: 0,
  name: "acoustic grand piano",
  percussion: false,
};

type TrackBuilder = {
  name: string;
  channel: MidiChannel;
  instrument: MidiInstrument;
  notes: { midi: number; ticks: number; durationTicks: number; velocity: number; noteOffVelocity: number; channel: MidiChannel }[];
  controlChanges: Map<number, MidiControlChange[]>;
  programChanges: MidiProgramChange[];
  pitchBends: MidiPitchBend[];
  sysex: MidiSysEx[];
  meta: MidiMetaEvent[];
  unknownMeta: MidiUnknownMeta[];
  endTick: number;
};

const newTrackBuilder = (channel: MidiChannel): TrackBuilder => ({
  name: "",
  channel,
  instrument: { ...DEFAULT_INSTRUMENT },
  notes: [],
  controlChanges: new Map(),
  programChanges: [],
  pitchBends: [],
  sysex: [],
  meta: [],
  unknownMeta: [],
  endTick: 0,
});

const finalizeTrack = (b: TrackBuilder): MidiTrack => {
  const cc = new Map<number, ReadonlyArray<MidiControlChange>>();
  for (const [k, v] of b.controlChanges) {
    cc.set(k, v.slice().sort((a, c) => a.ticks - c.ticks));
  }
  return {
    name: b.name,
    channel: b.channel,
    instrument: b.instrument,
    notes: b.notes
      .slice()
      .sort((a, c) => a.ticks - c.ticks || a.midi - c.midi),
    controlChanges: cc,
    programChanges: b.programChanges.slice().sort((a, c) => a.ticks - c.ticks),
    pitchBends: b.pitchBends.slice().sort((a, c) => a.ticks - c.ticks),
    sysex: b.sysex,
    meta: b.meta.slice().sort((a, c) => a.ticks - c.ticks),
    unknownMeta: b.unknownMeta.slice().sort((a, c) => a.ticks - c.ticks),
  };
};

const NOTE_OFF_VELOCITY = 64 / 127;

/**
 * Закрывает ближайшую предыдущую незакрытую ноту с тем же `(midi, channel)`
 * в LIFO-порядке (последняя открытая нота). Если подходящей ноты нет —
 * событие дропается (orphan note-off). Это общая реализация для `0x80`
 * (NoteOff) и `NoteOn(midi, 0)` (running-note convention); matching
 * учитывает канал, чтобы overlap одинаковой ноты на разных каналах
 * закрывал только «свою» ноту.
 */
const closeOpenNote = (
  builder: TrackBuilder,
  absTick: number,
  channel: MidiChannel,
  midi: number,
  offVelNormalized: number
): void => {
  for (let i = builder.notes.length - 1; i >= 0; i -= 1) {
    const n = builder.notes[i]!;
    if (n.midi === midi && n.channel === channel && n.durationTicks === 0) {
      n.durationTicks = Math.max(1, absTick - n.ticks);
      n.noteOffVelocity = offVelNormalized;
      return;
    }
  }
};

const channelEventLength = (high: number): number => {
  if (high === 0x80 || high === 0x90 || high === 0xa0 || high === 0xb0 || high === 0xe0) return 2;
  if (high === 0xc0 || high === 0xd0) return 1;
  return 0;
};

const handleChannelEvent = (
  builder: TrackBuilder,
  absTick: number,
  channel: MidiChannel,
  high: number,
  body: Uint8Array,
  dataStart: number
): void => {
  if (high === 0x90 && body[dataStart + 1] === 0) {
    // NoteOn(midi, 0) = running-note convention (NoteOff). Закрываем
    // предыдущую ноту; orphan-событие дропается.
    closeOpenNote(builder, absTick, channel, body[dataStart]!, NOTE_OFF_VELOCITY);
    return;
  }
  if (high === 0x90) {
    builder.notes.push({
      midi: body[dataStart]!,
      ticks: absTick,
      durationTicks: 0,
      velocity: body[dataStart + 1]! / MIDI_VELOCITY_MAX,
      noteOffVelocity: NOTE_OFF_VELOCITY,
      channel,
    });
    return;
  }
  if (high === 0x80) {
    closeOpenNote(
      builder,
      absTick,
      channel,
      body[dataStart]!,
      body[dataStart + 1]! / MIDI_VELOCITY_MAX
    );
    return;
  }
  if (high === 0xa0) {
    // polyPressure — игнорируем.
    return;
  }
  if (high === 0xb0) {
    const number = body[dataStart]!;
    const value = body[dataStart + 1]!;
    const list = builder.controlChanges.get(number) ?? [];
    list.push({ ticks: absTick, number, value, channel });
    builder.controlChanges.set(number, list);
    return;
  }
  if (high === 0xc0) {
    const program = body[dataStart]!;
    const meta = getInstrumentMeta(program, channel);
    builder.instrument = {
      ...builder.instrument,
      number: program,
      name: meta.name,
      family: meta.family,
      percussion: meta.percussion,
    };
    builder.programChanges.push({ ticks: absTick, value: program, channel });
    return;
  }
  if (high === 0xd0) {
    // channelPressure — игнорируем.
    return;
  }
  if (high === 0xe0) {
    const lsb = body[dataStart]!;
    const msb = body[dataStart + 1]!;
    const raw = (msb << 7) | lsb;
    const normalized = (raw - 8192) / 8192;
    builder.pitchBends.push({ ticks: absTick, value: Math.max(-1, Math.min(1, normalized)), channel });
    return;
  }
};

const parseTrack = (
  body: Uint8Array,
  defaultChannel: MidiChannel
): { track: MidiTrack; lastTick: number } => {
  const builder = newTrackBuilder(defaultChannel);
  builder.instrument.percussion = defaultChannel === DRUM_CHANNEL;
  let pos = 0;
  let absTick = 0;
  let runningStatus: number | null = null;
  const end = body.length;

  while (pos < end) {
    const delta = readVLQ(body, pos, end);
    if (!delta) break;
    absTick += delta.value;
    pos = delta.next;
    if (pos >= end) break;

    let status = body[pos]!;
    if (status < 0x80) {
      if (runningStatus === null) break;
      const high = runningStatus & 0xf0;
      const channel = (runningStatus & 0x0f) as MidiChannel;
      const dataLen = channelEventLength(high);
      if (pos + dataLen > end) break;
      handleChannelEvent(builder, absTick, channel, high, body, pos);
      pos += dataLen;
      continue;
    }
    pos += 1;

    if (status === 0xff) {
      const parsed = parseMetaEvent(body, pos, end);
      if (!parsed) break;
      const { metaType, dataStart, dataEnd } = parsed.meta;
      const data = body.subarray(dataStart, dataEnd);
      pos = parsed.next;
      if (metaType === 0x2f) {
        builder.endTick = absTick;
        break;
      }
      if (TEXT_META_TYPES.has(metaType)) {
        const text = decodeUtf8(data, 0, data.length);
        if (metaType === 0x03) {
          builder.name = text;
        }
        builder.meta.push({
          ticks: absTick,
          type: metaTypeName(metaType),
          text,
        });
      } else if (!UNKNOWN_META_TYPES.has(metaType)) {
        builder.unknownMeta.push({
          ticks: absTick,
          type: metaType,
          bytes: data.slice(),
        });
      }
      continue;
    }

    if (status === 0xf0 || status === 0xf7) {
      const lenVLQ = readVLQ(body, pos, end);
      if (!lenVLQ) break;
      const payloadStart = lenVLQ.next;
      const payloadEnd = Math.min(payloadStart + lenVLQ.value, end);
      const bytes = body.subarray(payloadStart, payloadEnd);
      builder.sysex.push({ ticks: absTick, bytes, isF0: status === 0xf0 });
      pos = payloadEnd;
      runningStatus = null;
      continue;
    }

    if (status >= 0xf0) continue;

    runningStatus = status;
    const high = status & 0xf0;
    const channel = (status & 0x0f) as MidiChannel;
    const dataStart = pos;
    const dataLen = channelEventLength(high);
    if (dataStart + dataLen > end) break;
    handleChannelEvent(builder, absTick, channel, high, body, dataStart);
    pos = dataStart + dataLen;
  }

  builder.endTick = absTick;
  const track = finalizeTrack(builder);
  // Bank-select detection: только CC0 (Bank MSB) == 120 (0x78) помечает
  // не-9 канал как Rhythm Channel (GM2 RP-035 §2.1 — MSB 78H = Rhythm,
  // 79H = Melody). CC0 == 121 (0x79) — Melody, не ударные; CC32 (банк-LSB)
  // роль канала не переключает. Раньше CC0/CC32 == 120/121 помечали
  // percussion, что давало ложные срабатывания на XG-файлах с role-switching
  // (issue #5).
  const cc0 = track.controlChanges.get(0) ?? [];
  const isDrumBank = cc0.some((c) => c.value === 120);
  if (isDrumBank) {
    return {
      track: {
        ...track,
        instrument: { ...track.instrument, percussion: true },
      },
      lastTick: builder.endTick,
    };
  }
  return { track, lastTick: builder.endTick };
};

type HeaderBuilder = {
  name: string;
  tempos: MidiTempo[];
  timeSignatures: { ticks: number; timeSignature: [number, number] }[];
  keySignatures: { ticks: number; key: string; scale: "major" | "minor" }[];
  meta: { ticks: number; type: string; text?: string }[];
};

/**
 * Сканирует трек на наличие meta-событий уровня шапки (setTempo,
 * timeSignature, keySignature, текстовые copyright/text/lyrics),
 * которые в исходном SMF часто лежат в треке 0.
 */
const harvestHeaderEvents = (body: Uint8Array): HeaderBuilder => {
  const header: HeaderBuilder = {
    name: "",
    tempos: [],
    timeSignatures: [],
    keySignatures: [],
    meta: [],
  };
  let pos = 0;
  let absTick = 0;
  let runningStatus: number | null = null;
  const end = body.length;
  let trackNameFound = false;

  while (pos < end) {
    const delta = readVLQ(body, pos, end);
    if (!delta) break;
    absTick += delta.value;
    pos = delta.next;
    if (pos >= end) break;

    let status = body[pos]!;
    if (status < 0x80) {
      if (runningStatus === null) break;
      pos += 1;
      const high = runningStatus & 0xf0;
      pos += high === 0xc0 || high === 0xd0 ? 1 : 2;
      continue;
    }
    pos += 1;

    if (status === 0xff) {
      const parsed = parseMetaEvent(body, pos, end);
      if (!parsed) break;
      const { metaType, dataStart, dataEnd } = parsed.meta;
      const data = body.subarray(dataStart, dataEnd);
      pos = parsed.next;
      if (metaType === 0x2f) break;
      if (metaType === 0x51) {
        const tempo = parseTempoEvent(absTick, data);
        if (tempo) header.tempos.push(tempo);
        continue;
      }
      if (metaType === 0x58) {
        const ts = parseTimeSignatureEvent(absTick, data);
        if (ts) header.timeSignatures.push(ts);
        continue;
      }
      if (metaType === 0x59) {
        const ks = parseKeySignatureEvent(absTick, data);
        if (ks) header.keySignatures.push(ks);
        continue;
      }
      if (metaType === 0x03) {
        if (!trackNameFound) {
          header.name = decodeUtf8(data, 0, data.length);
          trackNameFound = true;
        }
        continue;
      }
      if (TEXT_META_TYPES.has(metaType)) {
        header.meta.push({
          ticks: absTick,
          type: metaTypeName(metaType),
          text: decodeUtf8(data, 0, data.length),
        });
        continue;
      }
      continue;
    }

    if (status === 0xf0 || status === 0xf7) {
      const lenVLQ = readVLQ(body, pos, end);
      if (!lenVLQ) break;
      pos = lenVLQ.next + Math.min(lenVLQ.value, end - lenVLQ.next);
      runningStatus = null;
      continue;
    }

    runningStatus = status;
    const high = status & 0xf0;
    pos += high === 0xc0 || high === 0xd0 ? 1 : 2;
  }

  return header;
};

const inferDefaultChannel = (body: Uint8Array): MidiChannel => {
  let pos = 0;
  let absTick = 0;
  let runningStatus: number | null = null;
  const end = body.length;
  while (pos < end) {
    const delta = readVLQ(body, pos, end);
    if (!delta) break;
    absTick += delta.value;
    pos = delta.next;
    if (pos >= end) break;
    let status = body[pos]!;
    if (status < 0x80) {
      if (runningStatus === null) break;
      pos += 1;
      continue;
    }
    pos += 1;
    if (status === 0xff) {
      const parsed = parseMetaEvent(body, pos, end);
      if (!parsed) break;
      pos = parsed.next;
      if (parsed.meta.metaType === 0x2f) break;
      continue;
    }
    if (status === 0xf0 || status === 0xf7) {
      const lenVLQ = readVLQ(body, pos, end);
      if (!lenVLQ) break;
      pos = lenVLQ.next + Math.min(lenVLQ.value, end - lenVLQ.next);
      runningStatus = null;
      continue;
    }
    if (status >= 0xf0) continue;
    const ch = (status & 0x0f) as MidiChannel;
    return ch;
  }
  return 0;
};

const isMtrkChunk = (input: Uint8Array, pos: number): boolean =>
  pos + 3 < input.length &&
  input[pos] === MTRK_BYTES[0] &&
  input[pos + 1] === MTRK_BYTES[1] &&
  input[pos + 2] === MTRK_BYTES[2] &&
  input[pos + 3] === MTRK_BYTES[3];

export const parseMidi = (input: Uint8Array): Midi => {
  if (input.length < 14) {
    throw new MidiParseError("MIDI file too short", 0);
  }
  if (!startsWith(input, 0, MTHD)) {
    throw new MidiParseError("Not a Standard MIDI File (missing MThd)", 0);
  }

  const format = readUint16BE(input, 8) as 0 | 1 | 2;
  if (format > 2) {
    throw new MidiParseError(`Unsupported MIDI format: ${format}`, 8);
  }
  const division = readUint16BE(input, 12);
  const ppq = division & 0x7fff;
  if (ppq === 0) {
    throw new MidiParseError("SMPTE-timed MIDI files are not supported", 12);
  }

  let pos = 14;
  const headerBuilder: HeaderBuilder = {
    name: "",
    tempos: [],
    timeSignatures: [],
    keySignatures: [],
    meta: [],
  };
  const tracks: MidiTrack[] = [];
  let lastTick = 0;

  while (pos + 8 <= input.length) {
    const chunkLen = readUint32BE(input, pos + 4);
    const chunkDataStart = pos + 8;
    const chunkDataEnd = Math.min(chunkDataStart + chunkLen, input.length);

    if (isMtrkChunk(input, pos)) {
      const body = input.subarray(chunkDataStart, chunkDataEnd);

      // 1) Header-meta события (tempo / time-sig / key-sig / trackName)
      // собираются со ВСЕХ MTrk-чанков. `harvestHeaderEvents` — чистый
      // сканер без побочных эффектов на канальных событиях, так что
      // повторный вызов на «не-header» треке безвреден.
      const harvested = harvestHeaderEvents(body);
      if (harvested.name && !headerBuilder.name) {
        headerBuilder.name = harvested.name;
      }
      for (const t of harvested.tempos) headerBuilder.tempos.push(t);
      for (const t of harvested.timeSignatures) headerBuilder.timeSignatures.push(t);
      for (const t of harvested.keySignatures) headerBuilder.keySignatures.push(t);
      for (const m of harvested.meta) headerBuilder.meta.push(m);

      // 2) Канальные события (ноты, CC, pitch bend) парсятся из
      // КАЖДОГО MTrk. Раньше первый MTrk считался header-треком и
      // пропускался — это ломало format-0 файлы и любые файлы,
      // у которых header-meta лежит в треке с нотами. (Bug #1)
      const defaultChannel = inferDefaultChannel(body);
      const parsed = parseTrack(body, defaultChannel);
      tracks.push(parsed.track);
      if (parsed.lastTick > lastTick) lastTick = parsed.lastTick;
    }

    pos = chunkDataEnd;
  }

  // Дедупликация: после сбора header-meta из всех MTrk один и тот же
  // setTempo / time-sig / key-sig / text-meta мог встретиться несколько
  // раз (например, tempo в header-треке и продублированное в data-треке
  // — это ломало бы Report, buildPlaybackData и round-trip encode).
  // Оставляем последнее вхождение по `ticks` — это соответствует SMF-
  // семантике «последнее событие в тике побеждает».
  dedupByTick(headerBuilder.tempos);
  dedupByTick(headerBuilder.timeSignatures);
  dedupByTick(headerBuilder.keySignatures);
  dedupMeta(headerBuilder.meta);

  if (headerBuilder.tempos.length === 0) {
    headerBuilder.tempos.push({ ticks: 0, bpm: 120 });
  }
  headerBuilder.tempos.sort((a, b) => a.ticks - b.ticks);
  headerBuilder.timeSignatures.sort((a, b) => a.ticks - b.ticks);
  headerBuilder.keySignatures.sort((a, b) => a.ticks - b.ticks);
  headerBuilder.meta.sort((a, b) => a.ticks - b.ticks);

  const header: MidiHeader = {
    name: headerBuilder.name,
    ppq,
    tempos: headerBuilder.tempos,
    timeSignatures: headerBuilder.timeSignatures,
    keySignatures: headerBuilder.keySignatures,
    meta: headerBuilder.meta,
    durationSeconds: tickToSeconds(lastTick, ppq, headerBuilder.tempos),
  };

  return {
    format,
    header,
    tracks,
  };
};